import {
  FilterModel,
  FilterOption,
  FilterOptionsState,
} from '../model/ui/filter.model';
import { FilterType } from '../model/ui/filter.type';
import { ListSortOption } from '../model/ui/list-sort-option';
import { VideoPlacement } from '../model/dto/video/video-placement';
import {
  NO_PLACEMENT,
  VIDEO_STATUSES,
} from '../model/dto/video/video-constants';
import {
  VideoArchivedFilter,
  VideoListFilterField,
  VideoListFilterValues,
} from '../model/type/video-list-filter.types';
import { findPlacement } from './video-placement-utils';

type Translate = (key: string) => string;

export const VIDEO_ARCHIVED_FILTERS: VideoArchivedFilter[] = ['hide', 'show'];

export const DEFAULT_VIDEO_ARCHIVED_FILTER: VideoArchivedFilter = 'hide';

const getPlacementOptions = (
  selected: string | null,
  placements: VideoPlacement[] | undefined,
  translate: Translate
): FilterOption[] => {
  const isRetired =
    !!selected &&
    selected !== NO_PLACEMENT &&
    !findPlacement(placements ?? [], selected);
  const retiredOptions: FilterOption[] =
    selected && isRetired
      ? [
          {
            value: selected,
            label: selected,
            note: placements
              ? translate('videos.placement.retired')
              : undefined,
          },
        ]
      : [];

  return [
    {
      value: NO_PLACEMENT,
      label: 'videos.placement.notShown',
      translate: true,
    },
    ...retiredOptions,
    ...(placements ?? []).map(({ key, description }) => ({
      value: key,
      label: description,
      secondaryLabel: key,
    })),
  ];
};

export const getVideoListFilters = (
  values: VideoListFilterValues,
  placements: VideoPlacement[] | undefined,
  placementsState: FilterOptionsState | undefined,
  translate: Translate
): FilterModel[] => [
  {
    type: FilterType.radio,
    fieldName: VideoListFilterField.status,
    label: 'videos.filters.status',
    value: values.status ?? '',
    options: VIDEO_STATUSES.map((status) => ({
      value: status,
      label: `videos.status.${status}`,
      translate: true,
    })),
  },
  {
    type: FilterType.radio,
    fieldName: VideoListFilterField.placement,
    label: 'videos.filters.shownIn',
    value: values.placement ?? '',
    options: getPlacementOptions(values.placement, placements, translate),
    search: {
      label: 'videos.filters.searchPlacements',
      emptyLabel: 'videos.filters.noPlacementMatches',
    },
    optionsState: placementsState,
  },
  {
    type: FilterType.text,
    fieldName: VideoListFilterField.title,
    label: 'videos.filters.title',
    fieldLabel: 'videos.filters.titleContains',
    value: values.title,
  },
  {
    type: FilterType.radio,
    fieldName: VideoListFilterField.archived,
    label: 'videos.filters.archived',
    value: values.archived,
    defaultValue: DEFAULT_VIDEO_ARCHIVED_FILTER,
    options: VIDEO_ARCHIVED_FILTERS.map((archived) => ({
      value: archived,
      label: `videos.filters.archivedOptions.${archived}`,
      translate: true,
    })),
  },
];

export const getVideoListSortOptions = (
  translate: Translate
): ListSortOption[] => [
  {
    order_by: 'created_at',
    order_dir: 'desc',
    label: translate('videos.sortOptions.newest'),
  },
  {
    order_by: 'created_at',
    order_dir: 'asc',
    label: translate('videos.sortOptions.oldest'),
  },
  {
    order_by: 'updated_at',
    order_dir: 'desc',
    label: translate('videos.sortOptions.recentlyUpdated'),
  },
  {
    order_by: 'title',
    order_dir: 'asc',
    label: translate('videos.sortOptions.titleAscending'),
  },
  {
    order_by: 'title',
    order_dir: 'desc',
    label: translate('videos.sortOptions.titleDescending'),
  },
];
